import { useQuery } from '@apollo/client';
import {
  AddCircle as AddCircleIcon,
  ExpandLess,
  ExpandMore,
  FiberManualRecordRounded as FiberManualRecordRoundedIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { Collapse, List, ListItemIcon, ListItemText, ListItem as MenuItem, Typography, useTheme } from '@mui/material';
import { gql } from '__generated__/gql';
import { useExtendedIntl } from 'hooks/useExtendedIntl';
import { FunctionComponent, PropsWithChildren, useState } from 'react';
import { ErrorMessage } from 'shared/ErrorMessage';
import { LoadingBlock } from 'shared/LoadingBlock';
import { PermissionsViewDecider } from 'shared/PermissionsViewDecider';
import { APIPermission } from 'typeDeclarations/enums';
import { NonPaginatedConnection } from 'typeDeclarations/graphql/base-types';
import { TeamNode, UserNode } from 'typeDeclarations/graphql/nodes';
import { useUiPermissions } from 'utils/useUiPermissions';
import { CreateTeamDialog } from './CreateTeamDialog/CreateTeamDialog';
import { hashNumberFromString, teamsColorPalette } from './utils';

export const NUM_INITIAL_TEAMS = 3;

/**
 * Fragment and type created for the optimization purpose during the login process
 */
export interface TeamsMenuQueryFragmentData {
  sessionUser: Pick<UserNode, 'id' | 'modified' | 'numTeams'> & {
    teams: NonPaginatedConnection<Pick<TeamNode, '__typename' | '_id' | 'id' | 'modified' | 'name'>>;
  };
}

export const TEAMS_MENU_QUERY_FRAGMENT = gql(/* GraphQL */ `
  fragment TeamsMenuQueryFragment on Query {
    sessionUser {
      id
      modified
      numTeams
      teams(first: $teamsAmount, orderBy: "name") {
        edges {
          node {
            id
            _id
            modified
            name
          }
        }
      }
    }
  }
`);

type TeamsMenuQueryData = TeamsMenuQueryFragmentData;

interface TeamsMenuQueryVariables {
  teamsAmount: number;
}

const TEAMS_MENU_QUERY = gql(/* GraphQL */ `
  query teamsMenuQuery($teamsAmount: Int!) {
    ...TeamsMenuQueryFragment
  }
`);

interface TeamsMenuProps {
  onMoreTeamsClick: () => void;
  onTeamClick: (teamId: string) => void;
}

export const TeamsMenu: FunctionComponent<PropsWithChildren<TeamsMenuProps>> = ({ onMoreTeamsClick, onTeamClick }) => {
  const { formatMessage } = useExtendedIntl();
  const { hasStaffPermission } = useUiPermissions();
  const theme = useTheme();

  const [open, setOpen] = useState(false);
  const [openCreateNewTeamDialog, setOpenCreateNewTeamDialog] = useState(false);

  const { loading, error, data } = useQuery<TeamsMenuQueryData, TeamsMenuQueryVariables>(TEAMS_MENU_QUERY, {
    variables: {
      teamsAmount: NUM_INITIAL_TEAMS,
    },
  });

  const handleCreateNewTeamDialogClose = () => setOpenCreateNewTeamDialog(false);

  const handleCreateNewTeamDialogOpen = () => setOpenCreateNewTeamDialog(true);

  const handleClick = () => setOpen(!open);

  if (loading) return <LoadingBlock />;
  if (error || !data) return <ErrorMessage />;

  const { teams, numTeams } = data.sessionUser;

  if (!numTeams || numTeams < 2) {
    return null;
  }

  return (
    <>
      <MenuItem button onClick={handleClick} data-testid="menu-select-teams">
        <ListItemIcon>
          <PeopleIcon htmlColor={theme.palette.text.primary} />
        </ListItemIcon>
        <ListItemText>{formatMessage({ id: 'teams-menu.list-item-text' })}</ListItemText>
        {open ? <ExpandLess /> : <ExpandMore />}
      </MenuItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List disablePadding>
          {hasStaffPermission && (
            <PermissionsViewDecider requiredPermissions={[APIPermission.PerformChanges]}>
              <>
                <MenuItem button onClick={handleCreateNewTeamDialogOpen}>
                  <ListItemIcon>
                    <AddCircleIcon htmlColor={theme.palette.text.primary} />
                  </ListItemIcon>
                  <Typography variant="body1">{formatMessage({ id: 'create-team-button.title' })}</Typography>
                </MenuItem>
                <CreateTeamDialog open={openCreateNewTeamDialog} onClose={handleCreateNewTeamDialogClose} />
              </>
            </PermissionsViewDecider>
          )}
          {teams.edges.map(({ node: team }) => {
            const teamColorIndex = hashNumberFromString(team._id) % teamsColorPalette.length;
            const teamColor = teamsColorPalette[teamColorIndex];

            return (
              <MenuItem
                button
                key={team.id}
                onClick={(e) => {
                  e.preventDefault();
                  onTeamClick(team.id);
                }}
              >
                <ListItemIcon>
                  <FiberManualRecordRoundedIcon sx={{ transform: 'scale(0.5)' }} htmlColor={teamColor} />
                </ListItemIcon>
                <ListItemText primary={team.name} />
              </MenuItem>
            );
          })}
          {/* Is the total number of teams greater than number of listed teams? */}
          {numTeams > NUM_INITIAL_TEAMS && (
            <MenuItem button onClick={onMoreTeamsClick}>
              <ListItemIcon />
              <ListItemText
                primary={`${numTeams - NUM_INITIAL_TEAMS}${formatMessage({
                  id: 'teams-submenu.menu-item.primary-text',
                })}`}
                secondary={formatMessage({ id: 'teams-submenu.menu-item.secondary-text' })}
              />
            </MenuItem>
          )}
        </List>
      </Collapse>
    </>
  );
};
